import React, { useContext, useState } from 'react';
import {
  Box,
  Button,
  Modal,
  Select,
  SpaceBetween,
  TextContent,
  Icon,
  FormField,
  SelectProps,
} from '@cloudscape-design/components';
import { useModelRedeployment } from 'src/hooks/useModelRedeployment';
import { ApiContext } from 'src/context/apiContext';
import { Link } from 'src/components/Link';
import { OperatorModalContext } from 'src/context/operatorModalContext';
import { PermissionAwareButton } from 'src/components/PermissionAwareButton';

export const RedeployModal = () => {
  const { state } = useContext(ApiContext);
  const { redeployConfig, setRedeployConfig } =
    useContext(OperatorModalContext);
  const [loading, requestRedeployment] = useModelRedeployment();
  const [requestError, setRequestError] = useState<Error | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<
    SelectProps.ChangeDetail['selectedOption'] | null
  >(null);
  const [requestedRedeployment, setRequestedRedeployment] =
    useState<boolean>(false);

  const resetModal = () => {
    setRedeployConfig(null);
    setRequestedRedeployment(false);
  };

  if (requestedRedeployment) {
    return (
      <Modal
        onDismiss={resetModal}
        visible={true}
        header="Success"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={resetModal}>
                Stay here
              </Button>
              <Link
                to={`/accounts/${state.search.curAccountNo}/applications/${state.search.curApplicationId}/modelVersions/${state.search.curModelVersionId}/modelArtifacts/${state.search.curModelArtifactId}/operatorFlowJobs`}
              >
                <Button variant="primary">View Operator Flows</Button>
              </Link>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween direction="horizontal" size="s">
          <Icon name="status-positive" />
          <TextContent>
            <p>
              Successfully requested a model redeployment. In a few moments you
              should be able to view the new model in SageMaker and in MLPigeon
              (with &quot;-redeploy&quot; suffix). If you are redeploying a
              deployment job with &quot;online&quot; inference type, your
              SageMaker endpoint will also be updated.
            </p>
            <p>Click the button below to return to the operator flows page.</p>
          </TextContent>
        </SpaceBetween>
      </Modal>
    );
  }

  return (
    <Modal
      onDismiss={resetModal}
      visible={!!redeployConfig}
      header="Request Redeployment"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={resetModal}>
              Cancel
            </Button>
            <PermissionAwareButton
              loading={loading}
              variant="primary"
              onClick={async () => {
                /* istanbul ignore next (cant click the button if the modal is not visible) */
                if (!redeployConfig) return;
                const result = await requestRedeployment({
                  operatorName: redeployConfig.operatorName,
                  rollbackOperatorFlowId: redeployConfig.rollbackOperatorFlowId,
                  selectedRegion: selectedRegion?.value,
                });
                setRequestError(result?.error ? result.error : null);
                setRequestedRedeployment(!!result?.success);
              }}
              requiredPermission={'READ_AND_LIMITED_ACTION'}
              actionDescription={'redeploy models'}
            >
              Request Redeployment
            </PermissionAwareButton>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {redeployConfig?.regions && redeployConfig.regions.length > 1 ? (
          <>
            <TextContent>
              <p>
                Redeploying will create a <strong>new model deployment</strong>{' '}
                in the selected region, using the settings from this deployment.
              </p>
            </TextContent>
            <FormField label="Redeployment Region">
              <Select
                selectedOption={selectedRegion}
                onChange={({ detail }) =>
                  setSelectedRegion(detail.selectedOption)
                }
                options={redeployConfig?.regions.map((region: string) => ({
                  label: region,
                  value: region,
                }))}
                placeholder="Select a region"
              />
            </FormField>
          </>
        ) : (
          <TextContent>
            <p>
              Redeploying will create a <strong>new model deployment</strong>{' '}
              {selectedRegion ? (
                <span>
                  in
                  <strong> {selectedRegion.value}</strong>,
                </span>
              ) : (
                ''
              )}{' '}
              using the settings from this deployment. The redeployed SageMaker
              model (and endpoint config, if the deploy mode is
              &quot;online&quot;) will have &quot;-redeploy&quot; appended to
              their names.
            </p>
          </TextContent>
        )}
        {requestError && (
          <TextContent>
            <p className="error-text">{requestError.message}</p>
          </TextContent>
        )}
      </SpaceBetween>
    </Modal>
  );
};
